// Sidebar for the platform-admin panel. Mirrors the shape of the
// regular Sidebar (same .sidebar classes) but ships its own NAV_ITEMS
// and a "← Volver" link back to the operator's home dashboard.

import { Link, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../../lib/auth/actions";
import { useSession } from "../../lib/auth/useSession";
import { Icon } from "../../components/icons";

type AdminNavItem = {
  key: string;
  label: string;
  path: string;
  icon: keyof typeof Icon;
};

const ADMIN_NAV: AdminNavItem[] = [
  { key: "overview", label: "Overview", path: "/admin", icon: "Home" },
  { key: "orgs", label: "Organizaciones", path: "/admin/orgs", icon: "Users" },
  { key: "carga", label: "Carga masiva", path: "/admin/carga-masiva", icon: "Inbox" },
];

function initialsFor(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join("") || "?"
  );
}

function isActive(pathname: string, item: AdminNavItem): boolean {
  if (item.path === "/admin") return pathname === "/admin";
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

export function AdminSidebar() {
  const session = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const userName = session?.user.displayName ?? "Sin sesión";

  return (
    <aside className="sidebar">
      <div className="sidebar__brand">
        <span className="sidebar__brand-name">BitácoraPro</span>
        <span className="sidebar__brand-tag">Admin</span>
      </div>

      <div className="sidebar__org">
        <div className="sidebar__org-mark">PA</div>
        <div className="sidebar__org-meta">
          <div className="sidebar__org-name">Platform admin</div>
          <div className="sidebar__org-sub">Cross-org operator</div>
        </div>
      </div>

      <nav className="sidebar__nav" aria-label="Panel admin">
        <div className="sidebar__group">
          <div className="sidebar__group-label">Plataforma</div>
          {ADMIN_NAV.map((n) => {
            const IconC = Icon[n.icon];
            const active = isActive(pathname, n);
            return (
              <Link
                key={n.key}
                to={n.path}
                aria-current={active ? "page" : undefined}
                className={["sidebar__link", active ? "sidebar__link--active" : ""]
                  .filter(Boolean)
                  .join(" ")}
              >
                <IconC />
                <span>{n.label}</span>
              </Link>
            );
          })}
        </div>

        <div className="sidebar__group">
          <div className="sidebar__group-label">Volver</div>
          <Link to="/" className="sidebar__link">
            <Icon.Home />
            <span>Dashboard</span>
          </Link>
        </div>
      </nav>

      <div className="sidebar__foot">
        <div className="sidebar__avatar">{initialsFor(userName)}</div>
        <div className="sidebar__user">
          <div className="sidebar__user-name">{userName}</div>
          <div className="sidebar__user-role">Platform admin</div>
        </div>
        <button
          type="button"
          className="sidebar__user-action"
          title="Cerrar sesión"
          aria-label="Cerrar sesión"
          onClick={() => void handleLogout()}
        >
          <Icon.LogOut s={16} />
        </button>
      </div>
    </aside>
  );
}
